import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { CalendarIcon, UserIcon, TagIcon, ChevronLeftIcon, ShareIcon, BookmarkIcon, FacebookIcon, TwitterIcon, LinkedinIcon, MailIcon, ArrowRightIcon } from 'lucide-react';
export const ArticlePage = () => {
  const {
    articleId
  } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedArticles, setRelatedArticles] = useState([]);
  useEffect(() => {
    // Simulate fetching article data
    const fetchArticle = () => {
      setLoading(true);
      // Mock articles data - in a real app, this would be fetched from an API
      const mockArticles = [{
        id: 'future-microbiome',
        title: 'The Future of Microbiome Research: 2025 Outlook',
        type: 'Article',
        date: 'January 15, 2024',
        author: 'Dr. Maria Chen',
        authorTitle: 'Director of Research, Ukweli Institute',
        authorImage: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        image: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
        excerpt: 'An in-depth analysis of emerging trends and breakthrough technologies in microbiome science that will shape the next decade of research and therapeutics.',
        topics: ['Microbiome', 'Research Methods'],
        readTime: '8 min read',
        content: [{
          type: 'paragraph',
          content: "The field of microbiome research stands at a pivotal moment. After a decade of rapid growth and discovery, we're now poised to translate our understanding of the human microbiome into practical applications that could transform medicine, nutrition, and our approach to human health."
        }, {
          type: 'heading',
          content: 'Emerging Technologies Driving Discovery'
        }, {
          type: 'paragraph',
          content: "Several technological breakthroughs are accelerating our ability to study and manipulate the microbiome with unprecedented precision. Single-cell sequencing now allows us to analyze individual microbial cells within complex communities, revealing functional differences that were previously invisible. Culturomics approaches have expanded our ability to grow previously 'unculturable' microbes, opening new avenues for studying their biology and potential therapeutic applications."
        }, {
          type: 'paragraph',
          content: 'Perhaps most exciting is the integration of artificial intelligence with microbiome research. Machine learning algorithms are now being trained on vast microbiome datasets, identifying patterns and correlations that human researchers might miss. These AI systems are helping to predict how microbiome compositions might respond to different interventions, from dietary changes to probiotic supplementation.'
        }, {
          type: 'image',
          url: 'https://images.unsplash.com/photo-1581093457331-7db10a89a05d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
          caption: 'Advanced sequencing technologies are revealing unprecedented details about microbial communities.'
        }, {
          type: 'heading',
          content: 'Precision Probiotics and Targeted Interventions'
        }, {
          type: 'paragraph',
          content: 'The era of generic, one-size-fits-all probiotics is coming to an end. By 2025, we anticipate a shift toward precision probiotic formulations designed for specific health conditions and individual microbiome profiles. These next-generation probiotics will be developed based on rigorous mechanistic research, with strains selected for their demonstrated ability to modulate specific biological pathways.'
        }, {
          type: 'paragraph',
          content: "Beyond probiotics, we're seeing promising developments in microbiome-based therapeutics. Fecal microbiota transplantation (FMT) has already proven effective for treating recurrent Clostridioides difficile infections, and research is underway to refine this approach for other conditions. Rather than transplanting entire microbial communities, future therapies may use defined consortia of beneficial microbes, carefully selected and tested for specific therapeutic effects."
        }, {
          type: 'quote',
          content: 'The microbiome field is transitioning from correlation to causation, from observation to intervention, and from general approaches to precision strategies tailored to individual needs.',
          author: 'Dr. Sarah Chen, Founder & Chief Scientific Officer, Ukweli Institute'
        }, {
          type: 'heading',
          content: 'Beyond the Gut: Exploring New Microbiome Frontiers'
        }, {
          type: 'paragraph',
          content: "While gut microbiome research continues to advance, we're also expanding our focus to other microbial communities in and on the human body. The skin microbiome is emerging as a critical factor in dermatological health, with potential applications in treating conditions from acne to eczema. Similarly, the oral microbiome is being studied for its connections to not only dental health but also systemic conditions like cardiovascular disease."
        }, {
          type: 'paragraph',
          content: 'Perhaps most intriguing is the growing research on the gut-brain axis, exploring how gut microbes influence neurological function and mental health. Early evidence suggests connections between microbiome composition and conditions ranging from depression to neurodegenerative diseases, opening new potential avenues for intervention.'
        }, {
          type: 'heading',
          content: 'Challenges and Ethical Considerations'
        }, {
          type: 'paragraph',
          content: 'As microbiome science advances, we must address several challenges. Standardization of research methods remains an issue, with different sampling, sequencing, and analysis techniques sometimes yielding inconsistent results. The field needs robust, reproducible protocols to ensure that findings can be validated across different studies and laboratories.'
        }, {
          type: 'paragraph',
          content: "We must also consider the ethical implications of microbiome research and interventions. Questions about ownership of microbiome data, the safety of experimental treatments, and equitable access to microbiome-based therapies all require careful consideration. At the Ukweli Institute, we're committed to transparent, ethical research that prioritizes both scientific rigor and human welfare."
        }, {
          type: 'heading',
          content: 'The Path Forward'
        }, {
          type: 'paragraph',
          content: 'The next five years promise to be transformative for microbiome research. As we move from basic science to practical applications, collaboration will be essential. The Ukweli Institute is fostering partnerships between academic researchers, healthcare providers, and industry stakeholders to accelerate the development and validation of microbiome-based solutions.'
        }, {
          type: 'paragraph',
          content: 'By 2025, we anticipate that microbiome testing will become a routine part of health assessment, with personalized recommendations for diet, lifestyle, and targeted interventions based on individual microbiome profiles. The era of truly personalized, microbiome-informed healthcare is on the horizon, and the potential benefits for human health are profound.'
        }, {
          type: 'conclusion',
          content: "As we look to the future of microbiome research, one thing is clear: this field will continue to surprise and inspire us with new discoveries about the microscopic communities that shape our health. At the Ukweli Institute, we're proud to be at the forefront of this scientific revolution, committed to rigorous research that translates into real-world benefits for human health."
        }]
      }, {
        id: 'probiotics-truth',
        title: 'The Truth About Probiotics: Separating Science from Marketing',
        type: 'Article',
        date: 'August 15, 2023',
        author: 'Dr. Sarah Chen',
        authorTitle: 'Founder & Chief Scientific Officer, Ukweli Institute',
        authorImage: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        image: 'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
        excerpt: 'An evidence-based analysis of common probiotic claims and what the research actually supports.',
        topics: ['Probiotics', 'Supplements'],
        readTime: '12 min read',
        content: [{
          type: 'paragraph',
          content: "Probiotics have become a multi-billion dollar industry, with products promising everything from improved digestion to enhanced immunity and even better mental health. But how many of these claims are supported by solid scientific evidence? In this article, we'll examine what the research actually tells us about probiotics and their effects on human health."
        }, {
          type: 'heading',
          content: 'What Are Probiotics, Really?'
        }, {
          type: 'paragraph',
          content: 'By definition, probiotics are live microorganisms that, when administered in adequate amounts, confer a health benefit on the host. This definition, established by the World Health Organization, highlights several important points: probiotics must be alive when administered, they must be provided in sufficient quantities, and they must have demonstrated health benefits.'
        }, {
          type: 'paragraph',
          content: "However, many products labeled as 'probiotics' fail to meet these criteria. Some contain strains that haven't been proven beneficial in human studies, while others may not contain enough viable organisms to have an effect. And in many cases, the specific health claims made on packaging aren't supported by robust clinical evidence."
        }, {
          type: 'image',
          url: 'https://images.unsplash.com/photo-1576671414121-aa0c81c869e3?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
          caption: 'Probiotic supplements come in many forms, but not all are created equal.'
        }, {
          type: 'heading',
          content: 'Strain Specificity: The Devil in the Details'
        }, {
          type: 'paragraph',
          content: 'One of the most common misconceptions about probiotics is that all strains of a particular species have the same effects. In reality, probiotic effects are strain-specific. For example, while certain strains of Lactobacillus rhamnosus have been shown to help prevent antibiotic-associated diarrhea, other strains of the same species might have completely different effects or no proven benefits at all.'
        }, {
          type: 'paragraph',
          content: 'This strain specificity is often obscured in marketing materials, which might prominently feature a genus and species name (like Lactobacillus acidophilus) without specifying the exact strain being used or whether that particular strain has been studied in humans.'
        }, {
          type: 'heading',
          content: 'What the Science Actually Supports'
        }, {
          type: 'paragraph',
          content: 'Despite the marketing hype, there are some well-established benefits of specific probiotic strains. The strongest evidence exists for:'
        }, {
          type: 'list',
          items: ['Prevention of antibiotic-associated diarrhea (several strains including Lactobacillus rhamnosus GG and Saccharomyces boulardii)', 'Treatment of infectious diarrhea (shortening duration by about one day)', 'Management of certain digestive conditions like irritable bowel syndrome (specific strains including Bifidobacterium infantis 35624)', 'Prevention of necrotizing enterocolitis in premature infants', 'Helping to maintain remission in ulcerative colitis (specific formulation VSL#3)']
        }, {
          type: 'paragraph',
          content: 'For many other commonly advertised benefits, the evidence is either mixed, preliminary, or nonexistent. Claims about probiotics improving immunity, mental health, skin conditions, or weight management typically lack robust clinical support, despite promising results in some laboratory or animal studies.'
        }, {
          type: 'quote',
          content: "The gap between what's been proven in clinical trials and what's claimed on product labels is often substantial. Consumers deserve better than marketing that gets ahead of the science.",
          author: 'Dr. James Rivera, Director of Communications & Outreach, Ukweli Institute'
        }, {
          type: 'heading',
          content: 'Survival and Colonization: Do They Reach Their Target?'
        }, {
          type: 'paragraph',
          content: "Another critical issue is whether probiotic organisms actually survive passage through the harsh acidic environment of the stomach and reach the intestines in viable form. Many products haven't been tested for this, and studies that have examined the question show widely varying results depending on the strains used and how the product is formulated."
        }, {
          type: 'paragraph',
          content: "Even when probiotics do reach the intestines alive, they typically don't establish permanent colonies. Most strains pass through the digestive system within days or weeks after supplementation stops. This transient colonization means that for ongoing benefits, continuous consumption is usually necessary."
        }, {
          type: 'heading',
          content: 'Quality Control Concerns'
        }, {
          type: 'paragraph',
          content: 'The probiotic market is plagued by quality control issues. Independent testing has found that many products contain fewer live organisms than claimed on their labels, while others may include different strains than those listed or even potentially harmful contaminants.'
        }, {
          type: 'paragraph',
          content: "In the United States, probiotics are regulated as dietary supplements, not pharmaceuticals, meaning they don't undergo the same rigorous pre-market approval process. This regulatory environment has allowed products of questionable quality and efficacy to proliferate."
        }, {
          type: 'heading',
          content: 'Making Informed Choices'
        }, {
          type: 'paragraph',
          content: 'So how can consumers make informed choices about probiotics? Here are some evidence-based recommendations:'
        }, {
          type: 'list',
          items: ['Look for products that specify exact strains, not just species', 'Check whether those specific strains have been studied in humans for your condition of interest', 'Choose products from reputable manufacturers that participate in third-party testing', 'Be wary of products making broad or miraculous health claims', 'Consult healthcare providers about specific probiotics for specific conditions', 'Consider whether dietary changes to support your natural microbiome might be more beneficial than supplements']
        }, {
          type: 'heading',
          content: 'The Future of Probiotics'
        }, {
          type: 'paragraph',
          content: 'Despite the current limitations and marketing excesses, the future of probiotic research is promising. Next-generation probiotics are being developed based on deeper understanding of the microbiome and specific mechanisms of action. These will likely include novel strains identified through advanced microbiome research, precisely targeted to address specific health conditions.'
        }, {
          type: 'paragraph',
          content: "At the Ukweli Institute, we're committed to advancing this field through rigorous, independent research that puts scientific evidence ahead of marketing claims. Our certification program evaluates probiotic products based on quality, safety, and demonstrated efficacy, helping consumers identify products that meet high scientific standards."
        }, {
          type: 'conclusion',
          content: 'Probiotics hold genuine promise for improving human health, but realizing that promise requires separating scientific fact from marketing fiction. By demanding evidence-based products and supporting independent research, consumers can help shape a future where probiotic benefits are both real and reliably delivered.'
        }]
      }, {
        id: 'diet-microbiome',
        title: 'How Diet Affects Your Microbiome: New Research Findings',
        type: 'Article',
        date: 'June 12, 2023',
        author: 'Dr. Michael Rodriguez',
        authorTitle: 'Nutrition Research Lead, Ukweli Institute',
        authorImage: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        image: 'https://images.unsplash.com/photo-1498837167922-ddd27525d352?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
        excerpt: 'Recent studies reveal surprising connections between specific dietary patterns and microbiome diversity.',
        topics: ['Nutrition', 'Microbiome'],
        readTime: '10 min read',
        content: [{
          type: 'paragraph',
          content: "The saying 'you are what you eat' has taken on new meaning as research increasingly shows that our diet shapes not just our own cells, but the trillions of microbial cells that inhabit our digestive tract. Recent findings are revealing just how quickly and dramatically our food choices can alter the gut microbiome, with potential implications for health conditions ranging from inflammatory bowel disease to metabolic disorders and even neurological health."
        }, {
          type: 'heading',
          content: 'Dietary Patterns and Microbiome Diversity'
        }, {
          type: 'paragraph',
          content: 'One of the most consistent findings in microbiome research is that dietary diversity promotes microbial diversity, which is generally associated with better health outcomes. A varied diet rich in different plant foods appears to support a more diverse and resilient microbial ecosystem.'
        }, {
          type: 'paragraph',
          content: "Recent studies have found that people who regularly consume more than 30 different plant foods weekly have significantly greater microbial diversity than those consuming fewer than 10 different plant foods. This diversity isn't just about fruits and vegetables—whole grains, legumes, nuts, seeds, herbs, and spices all contribute different fibers and phytochemicals that feed various beneficial microbes."
        }]
      }];
      const foundArticle = mockArticles.find(a => a.id === articleId);
      // Find related articles (excluding the current one)
      const related = mockArticles.filter(a => a.id !== articleId && (foundArticle ? a.topics.some(topic => foundArticle.topics.includes(topic)) : false)).slice(0, 3);
      setTimeout(() => {
        setArticle(foundArticle || null);
        setRelatedArticles(related);
        setLoading(false);
      }, 800); // Simulate network delay
    };
    fetchArticle();
  }, [articleId]);
  if (loading) {
    return <div className="min-h-screen pt-32 pb-12 flex flex-col items-center justify-start bg-gray-50" data-id="element-190">
        <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-191"></div>
        <p className="mt-4 text-gray-600" data-id="element-192">Loading article...</p>
      </div>;
  }
  if (!article) {
    return <div className="min-h-screen pt-32 pb-12 flex flex-col items-center justify-start bg-gray-50" data-id="element-193">
        <div className="text-center" data-id="element-194">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-195">
            Article Not Found
          </h2>
          <p className="text-gray-600 mb-8" data-id="element-196">
            The article you're looking for doesn't exist or has been removed.
          </p>
          <Link to="/media" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" data-id="element-197">
            <ChevronLeftIcon size={16} className="mr-2" data-id="element-198" />
            Back to Media & Insights
          </Link>
        </div>
      </div>;
  }
  const renderContent = content => {
    return content.map((block, index) => {
      switch (block.type) {
        case 'paragraph':
          return <p key={index} className="text-gray-700 mb-6 leading-relaxed" data-id="element-199">
              {block.content}
            </p>;
        case 'heading':
          return <h2 key={index} className="text-2xl font-bold text-gray-900 mb-4 mt-8" data-id="element-200">
              {block.content}
            </h2>;
        case 'image':
          return <figure key={index} className="mb-8" data-id="element-201">
              <img src={block.url} alt={block.caption || ''} className="w-full h-auto rounded-lg" data-id="element-202" />
              {block.caption && <figcaption className="text-sm text-gray-500 mt-2 italic text-center" data-id="element-203">
                  {block.caption}
                </figcaption>}
            </figure>;
        case 'quote':
          return <blockquote key={index} className="border-l-4 border-blue-600 pl-4 py-2 mb-6 italic bg-blue-50 p-6 rounded-r-lg" data-id="element-204">
              <p className="text-gray-700 mb-2" data-id="element-205">{block.content}</p>
              {block.author && <footer className="text-sm font-medium text-gray-600" data-id="element-206">
                  — {block.author}
                </footer>}
            </blockquote>;
        case 'list':
          return <ul key={index} className="list-disc pl-6 mb-6 space-y-2" data-id="element-207">
              {block.items.map((item, i) => <li key={i} className="text-gray-700" data-id="element-208">
                  {item}
                </li>)}
            </ul>;
        case 'conclusion':
          return <div key={index} className="bg-gray-50 p-6 rounded-lg mb-6 border-l-4 border-blue-600" data-id="element-209">
              <p className="text-gray-700 leading-relaxed" data-id="element-210">{block.content}</p>
            </div>;
        default:
          return null;
      }
    });
  };
  return <div className="bg-white w-full" data-id="element-211">
      {/* Hero Section */}
      <section className="relative pt-32 pb-12 bg-gray-50" data-id="element-212">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-213">
          <div className="mb-6" data-id="element-214">
            <Link to="/media" className="inline-flex items-center text-blue-600 hover:text-blue-800" data-id="element-215">
              <ChevronLeftIcon size={16} className="mr-1" data-id="element-216" />
              Back to Media & Insights
            </Link>
          </div>
          <div className="mb-8" data-id="element-217">
            <div className="flex flex-wrap items-center gap-2 mb-4" data-id="element-218">
              {article.topics.map(topic => <span key={topic} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-219">
                  <TagIcon size={12} className="mr-1" data-id="element-220" />
                  {topic}
                </span>)}
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-221">
                <CalendarIcon size={12} className="mr-1" data-id="element-222" />
                {article.readTime}
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight" data-id="element-223">
              {article.title}
            </h1>
            <div className="flex items-center mb-6" data-id="element-224">
              <img src={article.authorImage} alt={article.author} className="w-12 h-12 rounded-full mr-4 object-cover" data-id="element-225" />
              <div data-id="element-226">
                <p className="font-medium text-gray-900" data-id="element-227">{article.author}</p>
                <p className="text-sm text-gray-600" data-id="element-228">{article.authorTitle}</p>
              </div>
              <div className="ml-auto flex items-center text-sm text-gray-500" data-id="element-229">
                <CalendarIcon size={14} className="mr-1" data-id="element-230" />
                {article.date}
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Featured Image */}
      <div className="w-full h-96 relative overflow-hidden" data-id="element-231">
        <img src={article.image} alt={article.title} className="w-full h-full object-cover" data-id="element-232" />
      </div>
      {/* Article Content */}
      <section className="py-12 bg-white" data-id="element-233">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-234">
          {/* Social Sharing Sidebar */}
          <div className="hidden lg:block fixed left-8 top-1/2 transform -translate-y-1/2" data-id="element-235">
            <div className="flex flex-col space-y-4" data-id="element-236">
              <button className="p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors" data-id="element-237">
                <FacebookIcon size={20} className="text-gray-700" data-id="element-238" />
              </button>
              <button className="p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors" data-id="element-239">
                <TwitterIcon size={20} className="text-gray-700" data-id="element-240" />
              </button>
              <button className="p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors" data-id="element-241">
                <LinkedinIcon size={20} className="text-gray-700" data-id="element-242" />
              </button>
              <button className="p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors" data-id="element-243">
                <MailIcon size={20} className="text-gray-700" data-id="element-244" />
              </button>
              <div className="w-px h-8 bg-gray-300 mx-auto" data-id="element-245"></div>
              <button className="p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors" data-id="element-246">
                <BookmarkIcon size={20} className="text-gray-700" data-id="element-247" />
              </button>
            </div>
          </div>
          {/* Mobile Share Buttons */}
          <div className="lg:hidden flex justify-center mb-8" data-id="element-248">
            <div className="flex space-x-4" data-id="element-249">
              <button className="p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors" data-id="element-250">
                <FacebookIcon size={20} className="text-gray-700" data-id="element-251" />
              </button>
              <button className="p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors" data-id="element-252">
                <TwitterIcon size={20} className="text-gray-700" data-id="element-253" />
              </button>
              <button className="p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors" data-id="element-254">
                <LinkedinIcon size={20} className="text-gray-700" data-id="element-255" />
              </button>
              <button className="p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors" data-id="element-256">
                <MailIcon size={20} className="text-gray-700" data-id="element-257" />
              </button>
              <button className="p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors" data-id="element-258">
                <BookmarkIcon size={20} className="text-gray-700" data-id="element-259" />
              </button>
            </div>
          </div>
          {/* Article Text */}
          <div className="prose prose-lg max-w-none" data-id="element-260">
            {renderContent(article.content)}
          </div>
          {/* Author Bio */}
          <div className="mt-12 pt-8 border-t border-gray-200" data-id="element-261">
            <div className="flex items-center" data-id="element-262">
              <img src={article.authorImage} alt={article.author} className="w-16 h-16 rounded-full mr-4 object-cover" data-id="element-263" />
              <div data-id="element-264">
                <h3 className="font-bold text-gray-900 mb-1" data-id="element-265">
                  {article.author}
                </h3>
                <p className="text-gray-600 mb-2" data-id="element-266">{article.authorTitle}</p>
                <p className="text-sm text-gray-700" data-id="element-267">
                  Expert in microbiome research with over 15 years of experience
                  in gut health and therapeutic development.
                </p>
              </div>
            </div>
          </div>
          {/* Tags */}
          <div className="mt-8 flex flex-wrap gap-2" data-id="element-268">
            {article.topics.map(topic => <Link key={topic} to={`/media?topic=${topic}`} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors" data-id="element-269">
                {topic}
              </Link>)}
          </div>
          {/* Share and Save Buttons */}
          <div className="mt-8 flex justify-between items-center border-t border-b border-gray-200 py-4" data-id="element-270">
            <button className="inline-flex items-center text-gray-700 hover:text-blue-600 transition-colors" data-id="element-271">
              <ShareIcon size={18} className="mr-2" data-id="element-272" />
              Share this article
            </button>
            <button className="inline-flex items-center text-gray-700 hover:text-blue-600 transition-colors" data-id="element-273">
              <BookmarkIcon size={18} className="mr-2" data-id="element-274" />
              Save for later
            </button>
          </div>
        </div>
      </section>
      {/* Related Articles */}
      {relatedArticles.length > 0 && <section className="py-12 bg-gray-50" data-id="element-275">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-276">
            <h2 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-277">
              Related Articles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-278">
              {relatedArticles.map(related => <motion.div key={related.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300" data-id="element-279">
                  <Link to={`/article/${related.id}`} className="block h-48 overflow-hidden" data-id="element-280">
                    <img src={related.image} alt={related.title} className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" data-id="element-281" />
                  </Link>
                  <div className="p-6" data-id="element-282">
                    <div className="flex items-center text-sm text-gray-500 mb-2" data-id="element-283">
                      <CalendarIcon size={14} className="mr-1" data-id="element-284" />
                      <span data-id="element-285">{related.date}</span>
                    </div>
                    <Link to={`/article/${related.id}`} data-id="element-286">
                      <h3 className="font-bold text-lg mb-2 text-gray-900 hover:text-blue-600 transition-colors" data-id="element-287">
                        {related.title}
                      </h3>
                    </Link>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2" data-id="element-288">
                      {related.excerpt}
                    </p>
                    <Link to={`/article/${related.id}`} className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium text-sm" data-id="element-289">
                      Read article <ArrowRightIcon size={16} className="ml-1" data-id="element-290" />
                    </Link>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>}
      {/* Newsletter Sign Up */}
      <section className="py-12 bg-white" data-id="element-291">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-292">
          <div className="bg-blue-50 rounded-xl p-8 md:p-12" data-id="element-293">
            <div className="text-center mb-6" data-id="element-294">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-295">
                Stay Updated with Our Latest Research
              </h2>
              <p className="text-gray-600" data-id="element-296">
                Subscribe to our newsletter for evidence-based insights on
                microbiome science and wellness
              </p>
            </div>
            <div className="max-w-md mx-auto" data-id="element-297">
              <form className="flex flex-col sm:flex-row gap-2" data-id="element-298">
                <input type="email" placeholder="Your email address" className="flex-grow px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" required data-id="element-299" />
                <button type="submit" className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors whitespace-nowrap" data-id="element-300">
                  Subscribe
                </button>
              </form>
              <p className="text-xs text-gray-500 mt-2 text-center" data-id="element-301">
                We respect your privacy. Unsubscribe at any time.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>;
};